<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useNuxtApp } from '#app'
import { getGoodDetail } from '@/api'
import { confirmOrder, getItemSpuDetail, buyNowConfirmOrder, addToCart, buildFacebookEventPayload, sendFacebookEventSafely, sendTikTokEventSafely } from '@/api/mall'
import config from '~/config/index'
import { ElLoading, ElRate, ElButton, ElDivider, ElMessage, ElDialog } from 'element-plus'
import RateItem from './mall/product/RateItem.vue'
import AddToCartModal from './common/AddToCartModal.vue'
import QuantitySelector from '@/components/common/QuantitySelector.vue'
import { useWindowResize } from '~/composables/useWindowResize'
import { trackEvent } from '~/utils/analytics'
import { createEventTrackingContext, getRedirectTrackingQuery } from '~/utils/eventTracking'
import { $t,transferOssUrl } from '~/utils/index'
import { useCdnDomain } from '~/composables/useCdnDomain'
// 使用窗口大小监听 - 只用于小屏幕检测，避免样式冲突
const { windowWidth } = useWindowResize()
const { $customFetch } = useNuxtApp()

// 使用 CDN 域名替换
const { replaceCdnDomain } = useCdnDomain()

// 小屏幕检测 - 480px以下
const isSmallScreen = computed(() => windowWidth.value <= 480)

const route = useRoute()
// SSR-provided product detail (optional)
const props = defineProps<{ 
  ssrProductDetail?: any
  isModal?: boolean // 是否在弹窗模式下显示
}>()

// 定义 emit 事件
const emit = defineEmits(['addToCartSuccess'])
const buyTotalQuantity = ref(1)
const cartDialogOpen = ref(false)
const slideIndex = ref(0)
const summaryRef = ref<HTMLElement | null>(null)
const thumbsRef = ref<HTMLElement | null>(null)
const ratingValue = ref<number | undefined>(undefined)
const reviewsRef = ref<any>(null)
const commentNum = ref(0)
const showZoomButton = ref(false)
const totalReviews = ref(0)
const averageRating = ref(0)
const heroImageWidth = ref(500) // 图片宽度，方形500px，长方形400px
const heroStyle = computed(() => {
  if (isSmallScreen.value) {
    return { width: '100%' }
  }
  return { width: `${heroImageWidth.value}px` }
})
let detailId = (route.query && route.query.detailId) ? route.query.detailId : ''
const productInfo = ref<any>(props.ssrProductDetail || {})
const productError = ref(false)
async function getDetail() {
  const loadingInstance = ElLoading.service({
    lock: true,
    text: 'Loading',
    background: 'rgba(255, 255, 255, 0.5)',
  })

  try {
    const res = await getGoodDetail({
      param: {
        id: detailId || '',
      }
    })
    const skuIconUrl = ref("");
    // 处理新的数据结构
    if (res.data.dealerItemSkuDTOList) {
      res.data.dealerItemSkuDTOList = res.data.dealerItemSkuDTOList.map(
        (o: any) => {
          if (o.iconUrl) {
            skuIconUrl.value = o.iconUrl;
          }
          if (o.state == 0) {
            o.inventory = 0;
          }
          return o;
        }
      );
    }
    productInfo.value = res.data

    // 初始化规格选择
    initializeSpecSelection()

    productError.value = false
  }
  catch (error) {
    productError.value = true
  }
  finally {
    loadingInstance.close()
  }
}




const selectedAttributes = ref<any>({})
const selectedSku = ref<any>(null)

function handleSelectSpecValue(spec: any, attrValue: any) {
  selectedAttributes.value = {
    ...selectedAttributes.value,
    [spec.id]: attrValue.id,
  }

  // 根据选中的规格匹配SKU
  matchSelectedSku()

  // 更新当前显示的图片
  updateCurrentImage(attrValue.skuImage)

}

function attrValueIsSelected(specId: string, attrValueId: string) {
  return selectedAttributes.value[specId] === attrValueId
}

// 检查某个规格值是否有可用库存
function isSpecValueAvailable(specId: string, attrValueId: string) {
  if (!productInfo.value.dealerItemSkuDTOList) return false
  
  // 查找包含该规格值的SKU，检查是否有库存且状态可用
  return productInfo.value.dealerItemSkuDTOList.some((sku: any) => {
    return sku.spItemSkuSpecificationIds?.includes(`${specId}-${attrValueId}`) 
      && sku.state !== 0 
      && sku.inventory > 0
  })
}

// 更新当前显示的图片
function updateCurrentImage(imageUrl: string) {
  if (!imageUrl) return

  // 找到对应的图片索引
  const imageIndex = productImages.value.findIndex((img: any) => img.iconUrl === imageUrl)
  if (imageIndex !== -1) {
    slideIndex.value = imageIndex
  }
}

// 检测图片尺寸并设置宽度
function checkImageSize(imageUrl: string) {
  if (typeof window === 'undefined') {
    // SSR 渲染阶段无法创建 Image，直接使用默认宽度
    heroImageWidth.value = 500
    return
  }

  if (!imageUrl) {
    heroImageWidth.value = 500 // 默认值
    return
  }

  const img = new Image()
  img.onload = function() {
    const width = img.naturalWidth
    const height = img.naturalHeight
    // 判断是否为方形（宽高比在0.9-1.1之间认为是方形）
    const ratio = width / height
    if (ratio >= 0.9 && ratio <= 1.1) {
      heroImageWidth.value = 500 // 方形图片
    } else {
      heroImageWidth.value = 420 // 长方形图片
    }
  }
  img.onerror = function() {
    heroImageWidth.value = 500 // 加载失败时使用默认值
  }
  img.src = imageUrl
}

// 根据选中的规格匹配对应的SKU
function matchSelectedSku() {
  if (!productInfo.value.dealerItemSkuDTOList) return

  const selectedSkuId = findMatchingSku()
  if (selectedSkuId) {
    selectedSku.value = productInfo.value.dealerItemSkuDTOList.find((sku: any) => sku.id === selectedSkuId)
  }
}

// 将指定 SKU 的规格同步到选项上
function applySkuSelection(sku: any) {
  if (!sku?.dealerItemSkuSpecReqDTOs) return

  const nextSelection: Record<string, string> = {}
  sku.dealerItemSkuSpecReqDTOs.forEach((spec: any) => {
    if (spec.dealerItemSkuSpecDTO) {
      nextSelection[spec.id] = spec.dealerItemSkuSpecDTO.id
    }
  })

  if (Object.keys(nextSelection).length > 0) {
    selectedAttributes.value = nextSelection
  }
  selectedSku.value = sku
}

// 查找匹配的SKU - 根据 spItemSkuSpecificationIds 匹配
function findMatchingSku() {
  if (!productInfo.value.dealerItemSkuDTOList) return null

  // 构建选中的规格ID字符串，格式：specGroupId-specValueId,
  const selectedSpecIds = Object.keys(selectedAttributes.value).map(specGroupId => {
    const specValueId = selectedAttributes.value[specGroupId]
    return `${specGroupId}-${specValueId}`
  }).sort().join(',') + ','

  // 在 SKU 列表中查找匹配的
  const matchedSku = productInfo.value.dealerItemSkuDTOList.find((sku: any) => {
    if (!sku.spItemSkuSpecificationIds) return false
    
    // 将 SKU 的规格 ID 也排序后比较
    const skuSpecIds = sku.spItemSkuSpecificationIds.split(',').filter((s: string) => s.trim()).sort().join(',') + ','
    return skuSpecIds === selectedSpecIds
  })

  return matchedSku?.id
}

// 初始化规格选择
function initializeSpecSelection() {
  if (!productInfo.value.dealerItemSkuDTOList) return
  if (!productInfo.value.dealerItemSkuSpecDTOs) return

  // 重置选中的规格
  selectedAttributes.value = {}

  // 为每个规格组选择第一个有库存的规格值
  productInfo.value.dealerItemSkuSpecDTOs.forEach((specGroup: any) => {
    const specValues = specGroup.dealerItemSkuSpecReqDTOs || []
    
    // 如果只有一个规格值，直接选中（不判断库存）
    if (specValues.length === 1) {
      selectedAttributes.value[specGroup.id] = specValues[0].id
      return
    }
    
    // 如果有多个规格值，获取第一个有库存的规格值
    const firstAvailableSpecValue = specValues.find((specValue: any) => {
      // 检查是否有包含该规格值的可用SKU（state不为0且有库存）
      return productInfo.value.dealerItemSkuDTOList?.some((sku: any) => {
        return sku.spItemSkuSpecificationIds?.includes(`${specGroup.id}-${specValue.id}`) 
          && sku.state !== 0 
          && sku.inventory > 0
      })
    })

    if (firstAvailableSpecValue) {
      // specGroup.id 是规格组ID，firstAvailableSpecValue.id 是规格值ID
      selectedAttributes.value[specGroup.id] = firstAvailableSpecValue.id
    }
  })

  // 根据选中的规格匹配对应的SKU
  matchSelectedSku()

  // 如果匹配的SKU没有库存，尝试选择下一个有库存的规格组合
  if (selectedSku.value && (selectedSku.value.inventory <= 0 || selectedSku.value.state === 0)) {
    findNextAvailableSku()
  }

  // 设置默认图片索引
  if (selectedSku.value) {
    const imageIndex = productImages.value.findIndex((img: any) => img.id === selectedSku.value.id)
    if (imageIndex !== -1) {
      slideIndex.value = imageIndex
    }
  }
}

// 查找下一个有库存的SKU组合
function findNextAvailableSku() {
  if (!productInfo.value.dealerItemSkuDTOList || !productInfo.value.dealerItemSkuSpecDTOs) return

  // 获取所有有库存的SKU
  const availableSkus = productInfo.value.dealerItemSkuDTOList.filter((sku: any) => {
    return sku.state !== 0 && sku.inventory > 0
  })

  if (availableSkus.length === 0) return

  // 按顺序尝试每个有库存的SKU
  for (const sku of availableSkus) {
    // 检查该SKU的规格组合是否完整
    if (sku.dealerItemSkuSpecReqDTOs && sku.dealerItemSkuSpecReqDTOs.length > 0) {
      // 应用该SKU的规格选择
      applySkuSelection(sku)
      
      // 验证当前选择的SKU是否有库存
      if (selectedSku.value && selectedSku.value.inventory > 0 && selectedSku.value.state !== 0) {
        return // 找到了有库存的SKU
      }
    }
  }
}

// 缩略图点击时同步 SKU 规格
function handleSkuImageClick(index: number) {
  // 如果所有SKU的iconUrl都为空，禁用切换
  if (allSkuIconUrlsEmpty.value) {
    return
  }

  slideIndex.value = index

  const clickedSkuId = productImages.value?.[index]?.id
  if (!clickedSkuId) return

  const clickedSku = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => sku.id === clickedSkuId)
  if (clickedSku) {
    applySkuSelection(clickedSku)
  }
}

// 当前基础价格
const currentPrice = computed(() => {
  if (selectedSku.value) {
    return selectedSku.value.finalPrice ?? selectedSku.value.price ?? selectedSku.value.skuPrice
  }
  return productInfo.value.finalPrice || productInfo.value.skuPrice || productInfo.value.price || getMinPrice(productInfo.value)
})

// 当前会员价
const currentMemberPrice = computed(() => {
  if (selectedSku.value && selectedSku.value.discountPrice) {
    return selectedSku.value.discountPrice
  }
  return productInfo.value.discountPrice
})

const vipIconUrl = 'http://sztjyshop.oss-cn-guangzhou.aliyuncs.com/imgs/2026/02-04/upload_pic_1770171482456_vip.png'

const showMemberPrice = computed(() => {
  const basePrice = Number(currentPrice.value)
  const memberPrice = Number(currentMemberPrice.value)
  if (!memberPrice || !basePrice) return false
  return memberPrice !== basePrice
})

// 当前SKU是否可用
const currentSkuAvailable = computed(() => {
  if (selectedSku.value) {
    return selectedSku.value.state !== 0 && selectedSku.value.inventory > 0
  }
  return productInfo.value.inStock !== false
})

const productSpecs = computed(() => {
  if (!productInfo.value.dealerItemSkuSpecDTOs) return []

  // 从 dealerItemSkuSpecDTOs 构建规格选项
  return productInfo.value.dealerItemSkuSpecDTOs.map((specGroup: any) => {
    return {
      id: specGroup.id,
      specName: specGroup.specName,
      children: (specGroup.dealerItemSkuSpecReqDTOs || [])
        .filter((specValue: any) => {
          // 过滤掉没有可用SKU的规格值（state为0的SKU）
          const hasAvailableSku = productInfo.value.dealerItemSkuDTOList?.some((sku: any) => {
            return sku.spItemSkuSpecificationIds?.includes(`${specGroup.id}-${specValue.id}`) && sku.state !== 0
          })
          return hasAvailableSku
        })
        .map((specValue: any) => {
          // 查找包含该规格值的可用SKU（state不为0），用于获取图片
          const matchingSku = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => {
            return sku.spItemSkuSpecificationIds?.includes(`${specGroup.id}-${specValue.id}`) && sku.state !== 0
          })
          
          return {
            id: specValue.id,
            specName: specValue.specName,
            skuImage: matchingSku?.iconUrl || ''
          }
        })
    }
  })
})



function getMinPrice(item: any) {
  if (!item.dealerItemSkuDTOList) return 0;
  const priceNumArrs = item.dealerItemSkuDTOList
    ? item.dealerItemSkuDTOList.map((o: any) => o.price)
    : [];
  
  // 检查数组是否为空
  if (priceNumArrs.length === 0) return 0;
  
  const minPrice = priceNumArrs.reduce((prev: number, next: number) => {
    return Math.min(prev, next);
  });
  return minPrice || 0;
}



// 路由
const router = useRouter()


const handleChange = () => {
  // 处理数量变化
}

const handleReviewSubmitted = (review: any) => {
  // 处理新评论逻辑
  console.log('新评论提交:', review)
  // 可以在这里更新评论数量等
  if (reviewsRef.value) {
    // 刷新评论数据
    reviewsRef.value.refreshComments()
  }
}

// 获取评价数据的方法
const getReviewData = async () => {
  if (reviewsRef.value) {
    try {
      // 从RateItem组件获取评价数据
      const reviews = reviewsRef.value.getReviews()
      const avgRating = reviewsRef.value.getAverageRating()
      
      // 更新本地数据
      totalReviews.value = reviews.length
      averageRating.value = parseFloat(avgRating) || 0
      ratingValue.value = averageRating.value
      commentNum.value = totalReviews.value
      
      console.log('评价数据:', {
        totalReviews: totalReviews.value,
        averageRating: averageRating.value,
        reviews: reviews
      })
    } catch (error) {
      console.error('获取评价数据失败:', error)
    }
  }
}

// 监听RateItem组件数据变化
const updateReviewData = () => {
  if (reviewsRef.value) {
    const reviews = reviewsRef.value.getReviews()
    const avgRating = reviewsRef.value.getAverageRating()
    
    totalReviews.value = reviews.length
    averageRating.value = parseFloat(avgRating) || 0
    ratingValue.value = averageRating.value
    commentNum.value = totalReviews.value
  }
}

// 图片导航函数
function previousImage() {
  // 如果所有SKU的iconUrl都为空，禁用切换
  if (allSkuIconUrlsEmpty.value) {
    return
  }
  if (slideIndex.value > 0) {
    slideIndex.value--
  }
}

function nextImage() {
  // 如果所有SKU的iconUrl都为空，禁用切换
  if (allSkuIconUrlsEmpty.value) {
    return
  }
  if (slideIndex.value < productImages.value.length - 1) {
    slideIndex.value++
  }
}

// 缩略图滚动函数
function scrollThumbsLeft() {
  if (thumbsRef.value) {
    // 计算每个缩略图的宽度（包括gap）
    const thumbWidth = thumbsRef.value.scrollWidth / productImages.value.length
    const scrollAmount = thumbWidth * 4 // 每次滚动4个缩略图的宽度
    thumbsRef.value.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    })
  }
}

function scrollThumbsRight() {
  if (thumbsRef.value) {
    // 计算每个缩略图的宽度（包括gap）
    const thumbWidth = thumbsRef.value.scrollWidth / productImages.value.length
    const scrollAmount = thumbWidth * 4 // 每次滚动4个缩略图的宽度
    thumbsRef.value.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    })
  }
}

// 判断是否需要显示滚动按钮（超过4张时）
const showThumbScrollButtons = computed(() => {
  return !isSmallScreen.value && productImages.value.length > 4
})

onMounted(async () => {
  if (props.ssrProductDetail) {
    productInfo.value = props.ssrProductDetail
    initializeSpecSelection()
    productError.value = false
  } else {
    await getDetail()
  }

  // Analytics 埋点：查看商品
  if (productInfo.value) {

    let val= productInfo.value.price|| 0;
    if(!val && productInfo.value?.dealerItemSkuDTOList&&productInfo.value?.dealerItemSkuDTOList.length>0) {
      val = productInfo.value?.dealerItemSkuDTOList[0]?.price|| 0;
    }
    trackEvent('ViewContent', {
      contents: [{
        content_id: productInfo.value.id,
        content_name: productInfo.value.title || productInfo.value.subject,
        content_type: 'product'
      }],
      value:val,
      currency: 'TWD',
      // 保留Google Analytics向后兼容性
      category: 'Product',
      action: 'view_product',
      label: productInfo.value.title || productInfo.value.subject
    })
  }

  // 延迟获取评价数据，确保RateItem组件已经加载完成
  setTimeout(() => {
    getReviewData()
  }, 1000)

  // 设置定期更新评价数据
  reviewUpdateInterval = setInterval(() => {
    updateReviewData()
  }, 3000) // 每3秒更新一次
})

// react to prop updates when navigating client-side
watch(() => props.ssrProductDetail, (val) => {
  if (val) {
    productInfo.value = val
    initializeSpecSelection()
    productError.value = false
  }
})

// 监听detailId变化，重新获取评价数据
watch(() => detailId, (newId) => {
  if (newId) {
    setTimeout(() => {
      getReviewData()
    }, 1500)
  }
})

// 检查所有SKU的iconUrl是否都为空
const allSkuIconUrlsEmpty = computed(() => {
  if (!productInfo.value.dealerItemSkuDTOList || productInfo.value.dealerItemSkuDTOList.length === 0) {
    return false
  }
  // 检查所有SKU的iconUrl是否都为空或无效
  return productInfo.value.dealerItemSkuDTOList.every((sku: any) => {
    const iconUrl = sku.iconUrl && sku.iconUrl.trim()
    return !iconUrl
  })
})

const productImages = computed(() => {
  // 如果所有SKU的iconUrl都为空，使用SPU的iconUrl作为唯一图片
  if (allSkuIconUrlsEmpty.value && productInfo.value.iconUrl) {
    // 使用第一个SKU的ID（如果有）或者使用SPU的ID
    const firstSku = productInfo.value.dealerItemSkuDTOList?.[0]
    return [{
      iconUrl: productInfo.value.iconUrl,
      id: firstSku?.id || productInfo.value.id,
      dealerItemSkuSpecReqDTOs: firstSku?.dealerItemSkuSpecReqDTOs,
      dealerItemSpuId: productInfo.value.id,
      spItemSkuSpecificationIds: firstSku?.spItemSkuSpecificationIds,
    }]
  }

  const attrMap: any[] = []

  productInfo.value.dealerItemSkuDTOList?.forEach((attr: any) => {
    const imageUrl = attr.iconUrl && attr.iconUrl.trim()
    if (!imageUrl) return

    attrMap.push({
      iconUrl: imageUrl,
      id: attr.id,
      dealerItemSkuSpecReqDTOs: attr.dealerItemSkuSpecReqDTOs,
      dealerItemSpuId: attr.dealerItemSpuId,
      spItemSkuSpecificationIds: attr.spItemSkuSpecificationIds,
    })
  })

  return attrMap
})

// 监听当前图片变化，检测图片尺寸
watch([slideIndex, productImages], () => {
  const currentImage = productImages.value[slideIndex.value]
  if (currentImage?.iconUrl) {
    checkImageSize(currentImage.iconUrl)
  }
}, { immediate: true })

// 定期更新评价数据
let reviewUpdateInterval: NodeJS.Timeout | null = null

// 组件卸载时清理定时器
onUnmounted(() => {
  if (reviewUpdateInterval) {
    clearInterval(reviewUpdateInterval)
  }
})

// 暴露方法给父组件
defineExpose({
  getReviewData,
  updateReviewData,
  getReviewStats: () => ({
    totalReviews: totalReviews.value,
    averageRating: averageRating.value,
    commentNum: commentNum.value
  })
})

const cartStore = useCartStore()
// 加入购物车 - 直接变更购物车数据，再打开抽屉
async function handleAddToCart() {
   
  if (!currentSkuAvailable.value) {
    ElMessage.warning($t('messages.specSoldOut'))
    return
  }
  console.log("selectedSkuselectedSku",selectedSku)
  console.log("productImages",productImages)

  // 校验 SKU：优先使用选中的SKU，否则回退到第一个可用的SKU
  let skuId = selectedSku.value?.id
  if (!skuId) {
    // 如果没有选中的SKU，尝试从SKU列表中获取第一个可用的SKU
    const firstAvailableSku = productInfo.value.dealerItemSkuDTOList?.find(
      (sku: any) => sku.state !== 0 && sku.inventory > 0
    )
    skuId = firstAvailableSku?.id
  }
  if (!skuId) {
    ElMessage.warning($t('messages.cannotGetSkuInfo'))
    return
  }

  // 构建规格信息
  const specsInfo = buildSpecsInfo()

  // 判断登录态（与 AddToCartModal 保持一致）
  const isLoggedIn = typeof window !== 'undefined' && !!window.localStorage.getItem(config.appTokenKey)
  const addToCartTrackingContext = createEventTrackingContext()
  const currentSkuData = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => sku.id === skuId) || {}
  const addToCartFacebookCustomData = {
    value: currentSkuData.price || productInfo.value.price || 0,
    currency: 'TWD',
    contentIds: skuId ? [skuId] : [],
    contentType: 'product'
  }
  const addToCartTikTokCustomData = {
    ...addToCartFacebookCustomData,
    contentName: productInfo.value.title || productInfo.value.subject || ''
  }

  try {
    if (isLoggedIn) {
      // 已登录：直接调接口写入购物车
      const facebookConversionPayload = buildFacebookEventPayload({
        eventId: addToCartTrackingContext.eventId,
        eventSourceUrl: addToCartTrackingContext.eventSourceUrl,
        eventName: 'AddToCart',
        customData: addToCartFacebookCustomData
      })
      const response = await $customFetch('/mall/admin/mallShoppingCart/add?isShop=1', {
        method: 'POST',
        body: {
          param: {
            buyAmount: buyTotalQuantity.value,
            specsInfo,
            dealerItemSkuId: skuId,
            dealerShopLiveStreamerId: '',
            eventId: addToCartTrackingContext.eventId,
            eventSourceUrl: typeof window !== 'undefined' ? window.location.href : addToCartTrackingContext.eventSourceUrl,
            facebookConversionRequestDTO: facebookConversionPayload?.body?.param || null
          }
        }
      })

      if (!response?.success) {
        ElMessage.error(response?.msg || $t('messages.addToCartFailed'))
        return
      }
      ElMessage.success($t('messages.addToCartSuccess'))
      void sendTikTokEventSafely({
        eventId: addToCartTrackingContext.eventId,
        eventSourceUrl: addToCartTrackingContext.eventSourceUrl,
        eventName: 'AddToCart',
        customData: addToCartTikTokCustomData
      })
      // Analytics 埋点：加购商品
      trackEvent('AddToCart', {
        contents: [{
          content_id: skuId,
          content_name: productInfo.value.title || productInfo.value.subject,
          content_type: 'product'
        }],
        value: addToCartFacebookCustomData.value,
        currency: 'TWD',
        eventId: addToCartTrackingContext.eventId,
        // 保留Google Analytics向后兼容性
        category: 'Product',
        action: 'add_to_cart',
        label: productInfo.value.title || productInfo.value.subject
      })
    } else {
      console.log('productInfo.value.sendTypes', productInfo.value)
      // 未登录：写入本地存储，与弹窗结构保持一致
      const LOCAL_CART_KEY = 'local_cart_items'
      // skuId 在前面已经处理了（优先使用 selectedSku，否则使用第一个可用SKU）
      const effectiveSkuId = skuId
      const newItem = {
        id: productInfo.value.id,
        title: productInfo.value.title || productInfo.value.subject,
        brand: 'SpStar',
        price: selectedSku.value?.price || productInfo.value.skuPrice || 0,
        buyAmount: buyTotalQuantity.value,
        image: selectedSku.value?.iconUrl || productInfo.value.iconUrl,
        options: specsInfo ? { specsInfo } : {},
        selectedSku: selectedSku.value,
        dealerItemSkuId: effectiveSkuId,
        spItemSpuId: productInfo.value.spItemSpuId,
        specsInfo,
        payWay: productInfo.value.payWay,
        sendTypes: productInfo.value.sendTypes
      }

      let cartItems = []
      try {
        const raw = typeof window !== 'undefined' ? window.localStorage.getItem(LOCAL_CART_KEY) : null
        cartItems = raw ? JSON.parse(raw) : []
      } catch (_) { cartItems = [] }

      // 与抽屉一致的合并规则：同 SKU + 同规格合并数量；若缺少 SKU，则退化为标题+规格比对
      const existingIndex = cartItems.findIndex((it: any) => {
        const sameSku = newItem.dealerItemSkuId && it.dealerItemSkuId === newItem.dealerItemSkuId
        const sameSpecs = it.specsInfo === newItem.specsInfo
        if (sameSku) return sameSpecs
        // 无法获取 SKU 时的兜底：按标题+规格合并，避免重复条目
        return !newItem.dealerItemSkuId && (it.title === newItem.title) && sameSpecs
      })
      if (existingIndex !== -1) {
        cartItems[existingIndex].buyAmount += newItem.buyAmount
      } else {
        cartItems.push(newItem)
      }
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(cartItems))
      }
      ElMessage.success($t('messages.addToCartSuccess'))
      void sendFacebookEventSafely({
        eventId: addToCartTrackingContext.eventId,
        eventSourceUrl: addToCartTrackingContext.eventSourceUrl,
        eventName: 'AddToCart',
        customData: addToCartFacebookCustomData
      })
      void sendTikTokEventSafely({
        eventId: addToCartTrackingContext.eventId,
        eventSourceUrl: addToCartTrackingContext.eventSourceUrl,
        eventName: 'AddToCart',
        customData: addToCartTikTokCustomData
      })
      // Analytics 埋点：加购商品（未登录状态）
      trackEvent('AddToCart', {
        contents: [{
          content_id: effectiveSkuId,
          content_name: productInfo.value.title || productInfo.value.subject,
          content_type: 'product'
        }],
        value: addToCartFacebookCustomData.value,
        currency: 'TWD',
        eventId: addToCartTrackingContext.eventId,
        // 保留Google Analytics向后兼容性
        category: 'Product',
        action: 'add_to_cart',
        label: productInfo.value.title || productInfo.value.subject
      })
    }

    cartStore.getCartData()

    // 如果在弹窗模式下，触发事件让父组件处理
    if (props.isModal) {
      emit('addToCartSuccess')
      return
    }

    // 打开抽屉展示最新购物车（已添加完成，使用view模式）
    cartDialogOpen.value = true
  } catch (error: any) {
    ElMessage.error(error?.message || $t('messages.addToCartFailedRetry'))
  }
}





const productImagePreviewList = computed(() => {
  return productImages.value
    .filter((item: any) => !!item.iconUrl)
    .map((item: any) => replaceCdnDomain(item.iconUrl))
})

// 当前主图片 URL（应用 CDN 域名替换）
const currentImageUrl = computed(() => {
  const currentImage = productImages.value[slideIndex.value]
  if (!currentImage?.iconUrl) return ''
  return replaceCdnDomain(currentImage.iconUrl)
})

// 获取缩略图 URL（应用 CDN 域名替换和 transferOssUrl）
const getThumbImageUrl = (iconUrl: string) => {
  if (!iconUrl) return ''
  const processedUrl = transferOssUrl(iconUrl, 300)
  return replaceCdnDomain(processedUrl)
}
// 立即购买方法 - 使用专门的立即购买接口
async function jumpPayPage() {
  if (!currentSkuAvailable.value) {
    ElMessage.warning($t('messages.specSoldOut'))
    return
  }

  // 检查是否有有效的SKU ID：优先使用选中的SKU，否则回退到第一个可用的SKU
  let skuId = selectedSku.value?.id
  if (!skuId) {
    // 如果没有选中的SKU，尝试从SKU列表中获取第一个可用的SKU
    const firstAvailableSku = productInfo.value.dealerItemSkuDTOList?.find(
      (sku: any) => sku.state !== 0 && sku.inventory > 0
    )
    skuId = firstAvailableSku?.id
  }
  if (!skuId) {
    ElMessage.warning($t('messages.cannotGetSkuInfoRefresh'))
    return
  }

  try {
    const loadingInstance = ElLoading.service({
      lock: true,
      text: $t('messages.confirmingOrder'),
      background: 'rgba(255, 255, 255, 0.5)',
    })

    // 构建规格信息字符串
    const specsInfo = buildSpecsInfo()

    // 构建立即购买的订单参数
    const confirmOrderData = {
      countryCode:"CN",
      confirmOrderSkuReqDTOList: [{
        countryCode:"CN",
        dealerShopLiveStreamerId: "", // 直播主播ID，立即购买为空
        dealerItemSkuId: skuId, // SKU ID
        spItemSkuSpecConfig: specsInfo, // 规格配置
        buyAmount: buyTotalQuantity.value, // 购买数量
        dealerItemSpuTitle: productInfo.value.title || productInfo.value.subject, // 商品标题
        dealerItemSpuIconUrl: selectedSku.value?.iconUrl || productInfo.value.iconUrl, // 商品图片
        inventory: selectedSku.value?.inventory || productInfo.value.inventory || 0, // 库存
        payWay: productInfo.value.payWay,
        sendTypes: productInfo.value.sendTypes // 配送方式
      }]
    }

    // 调用立即购买确认订单API
    const response = await buyNowConfirmOrder(confirmOrderData)

    loadingInstance.close()

    if (response.success) {
      // ElMessage.success('訂單確認成功，正在跳轉到支付頁面')

      // 将API返回的购物车数据存储到localStorage，供支付页面使用
      if (response.data) {
        const paymentCartData = {
          originalTotalAmount: response.data.originalTotalAmount || 0,
          realTotalAmount: response.data.realTotalAmount || 0,
          availableDiscounts :  response.data.availableDiscounts||[] , 
          bestDiscount :  response.data.bestDiscount , 
          otherData:response.data,
          estimatedDiscountAmount :  response.data.estimatedDiscountAmount , 
          originalTransportationExpenses: response.data.originalTransportationExpenses,
          realTransportationExpenses: response.data.realTransportationExpenses,
          confirmOrderSkuRespDTOList: response.data.confirmOrderSkuRespDTOList || [],
          confirmShopInfoRespDTOList: response.data.confirmShopInfoRespDTOList || null,
          payWay: productInfo.value.payWay,
          sendTypes: productInfo.value.sendTypes, // 配送方式
        }
        console.log('paymentCartData', paymentCartData)
        // 存储到localStorage
        if (process.client) {
          localStorage.setItem('cart', JSON.stringify(paymentCartData))
        }
      }

      // 跳转到支付页面
      await router.push({
        path: '/payment',
        query: getRedirectTrackingQuery()
      })
    } else {
      ElMessage.error(response.msg || $t('messages.orderConfirmFailed'))
    }
  } catch (error: any) {
    // 显示更具体的错误信息
    let errorMessage = $t('messages.buyNowFailed')
    if (error.response?.data?.msg) {
      errorMessage = error.response.data.msg
    } else if (error.message) {
      errorMessage = error.message
    }

    ElMessage.error(errorMessage)
  }
}

// 构建规格信息字符串
function buildSpecsInfo() {
  const specs: string[] = []
  
  // 如果没有选中任何规格，返回空字符串
  if (!selectedAttributes.value || Object.keys(selectedAttributes.value).length === 0) {
    return ''
  }
  
  Object.keys(selectedAttributes.value).forEach(specGroupId => {
    const specValueId = selectedAttributes.value[specGroupId]
    
    // 从 dealerItemSkuSpecDTOs 查找规格组
    const specGroup = productInfo.value.dealerItemSkuSpecDTOs?.find((s: any) => s.id === specGroupId)
    
    if (specGroup) {
      // 在规格组的子项中查找规格值
      const specValue = specGroup.dealerItemSkuSpecReqDTOs?.find((v: any) => v.id === specValueId)
      
      if (specValue) {
        specs.push(`${specGroup.specName}:${specValue.specName}`)
      }
    }
  })
  
  return specs.join(',')
}

</script>

<template>
  <div class="pdp" :class="{ 
    'small-screen': isSmallScreen
  }">
    <div class="content" :class="{ 'modal-content': isModal }">
      <div class="gallery">

        <div class="hero" :style="heroStyle" aria-hidden="true">
          <div class="hero-blank" @mouseenter="showZoomButton = true" @mouseleave="showZoomButton = false">
            <el-image 
              v-if="productImages[slideIndex]"
              preview-teleported 
              class="hero-image"
              :src="currentImageUrl"
              :preview-src-list="productImagePreviewList">
            </el-image>
            <!-- 放大按钮 -->
            <div class="zoom-button" :class="{ 'always-visible': isSmallScreen, 'hover-visible': !isSmallScreen && showZoomButton }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#0e1b4d"/>
                <path d="M12 9H10V7H12V9ZM12 13H10V11H12V13Z" fill="#0e1b4d"/>
              </svg>
            </div>
          </div>
        </div>
        <!-- 移动端页码指示器 -->
        <div class="mobile-pagination" v-if="isSmallScreen && !allSkuIconUrlsEmpty && productImages.length > 1">
          <div class="pagination-controls">
            <button class="nav-arrow" @click="previousImage" :disabled="slideIndex === 0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor"/>
              </svg>
            </button>
            <div class="page-indicator"><span>{{ slideIndex + 1 }}</span>/{{ productImages.length }}</div>
            <button class="nav-arrow" @click="nextImage" :disabled="slideIndex === productImages.length - 1">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
        
        <!-- PC端缩略图 -->
        <div class="thumbs-wrapper" v-if="!isSmallScreen && !allSkuIconUrlsEmpty" :class="{ 'has-scroll': showThumbScrollButtons }">
          <button 
            v-if="showThumbScrollButtons" 
            class="thumb-scroll-btn thumb-scroll-left" 
            @click="scrollThumbsLeft"
            :aria-label="$t('texts.scrollLeft')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor"/>
            </svg>
          </button>
          <div class="thumbs" ref="thumbsRef" :class="{ 'scrollable': showThumbScrollButtons }">
            <div v-for="(item, index) in productImages" :key="item" class="thumb"
              :class="{ active: slideIndex === index }" @click="handleSkuImageClick(index)">
              <img class="max-w-full h-full m-auto" referrerpolicy="no-referrer" :src="getThumbImageUrl(item.iconUrl)">
            </div>
          </div>
          <button 
            v-if="showThumbScrollButtons" 
            class="thumb-scroll-btn thumb-scroll-right" 
            @click="scrollThumbsRight"
            :aria-label="$t('texts.scrollRight')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>
      <!-- 商品信息 -->
      <div class="summary" ref="summaryRef">
        <h1 class="title">{{ productInfo.title || "" }}</h1>
        <div class="price-block">
          <div class="line price-row">
            <div class="tt-rating-text">
              <el-rate  v-model="ratingValue"  :disabled="true"></el-rate>
              <span class="demonstration"> {{ commentNum || '0' }} {{ $t('texts.reviewsCount') }}</span>
            </div>
          </div>
          <div class="line price-row">
            <div class="price-values" :class="{ 'has-member-price': showMemberPrice }">
              <template v-if="showMemberPrice">
                <div class="price-line non-member">
                  <span class="member-label">非會員價</span>
                  <span class="price-amount">{{ currentPrice }} TWD</span>
                </div>
                <div class="price-line member">
                  <img class="vip-icon" :src="vipIconUrl" alt="VIP" />
                  <span class="price-amount">{{ currentMemberPrice }} TWD</span>
                </div>
              </template>
              <template v-else>
                <span class="value current price-label">{{ currentPrice }} TWD</span>
              </template>
            </div>
            <div class="badges">
              <span v-if="!currentSkuAvailable" class="badge">{{ $t('texts.soldOut') }}</span>
            </div>
          </div>

        </div>

        <!-- <el-divider /> -->
        <!-- 规格选择 -->
        <div class="option" v-for="(spec, index) in productSpecs" :key="index">
          <div class="label">{{ spec.specName }}</div>
          <div class="option-iteme" v-for="attrValue in spec.children" :key="attrValue.id"
            :class="[
              attrValueIsSelected(spec.id, attrValue.id) ? 'active' : '',
              !isSpecValueAvailable(spec.id, attrValue.id) ? 'sold-out' : ''
            ]"
            @click="handleSelectSpecValue(spec, attrValue)">
            {{ attrValue.specName }}
          </div>
        </div>

        <div class="quantity">
          <QuantitySelector :label="$t('texts.quantity')" v-model="buyTotalQuantity" :min="1" :max="selectedSku && selectedSku.inventory || 999" :step="1"
            size="default" @change="handleChange" />
        </div>

        <div class="actions">
          <el-button class="add-to-cart" :class="{ 'sold-out-button': !currentSkuAvailable }" type="primary" :disabled="!currentSkuAvailable"
            @click="handleAddToCart">
            {{ currentSkuAvailable ? $t('actions.addBuyCart') : $t('texts.soldOut') }}
          </el-button>
          <el-button class="buy-now" :class="{ 'sold-out-button': !currentSkuAvailable }" style="margin-left: 0;" type="success" :disabled="!currentSkuAvailable"
            @click="jumpPayPage">{{ $t('actions.buyNow') }}</el-button>
        </div>

        <!-- <el-divider /> -->
        <div class="product-description" v-if="productInfo.simpleIntroduction && !isModal">
          <div v-html="productInfo.simpleIntroduction"></div>
        </div>
        <el-divider v-if="!isModal" />
      </div>
    </div>
    <RateItem
      v-if="!isModal"
      :product-id="productInfo.dealerShopId"
      :productInfo-spu-id="productInfo.id"
      :product-comment-id="productInfo.dealerInfoId"
      :product-name="productInfo.title"
      :product-brand="productInfo.brand"
      :product-image="currentImageUrl"
      @review-submitted="handleReviewSubmitted"
      ref="reviewsRef"
    />
    <ClientOnly>
      <AddToCartModal 
        v-if="!isModal"
        v-model="cartDialogOpen" 
        mode="view" 
        :product-info="productInfo" 
        :selected-sku="selectedSku"
        :selected-attributes="selectedAttributes" 
        :buy-total-quantity="buyTotalQuantity" 
      />
    </ClientOnly>
  </div>
</template>

<style scoped>
.pdp {
  min-width: 1200px;
  width: 100%;
  
  margin: 0;
  padding: 0;
  background: #eff0f5;
}

.pdp :deep(.el-rate.is-disabled .el-rate__item) {
  color: #e6e6e6;
}
.price-label{
  margin:5px 0px;
  font-size: 19px;
  color:rgb(14, 27, 77);
}
/* 移动端适配 - 参考头部和底部的实现方式 */
@media (max-width: 768px) {
  .pdp {
    min-width: auto;
    width: 100%;
    overflow-x: hidden;
  }
}

.content {
  display: grid;
  grid-template-columns: 1fr;
  padding-top: 20px;
  justify-content: center;
  gap: 20px;
  margin: 0 auto;
  padding-left: 16px;
  padding-right: 16px;
  width: 100%;
  box-sizing: border-box;

}

/* 移动端内容区域优化 */
@media (max-width: 768px) {
  .content {
    grid-template-columns: 1fr;
    padding: 15px 10px;
    gap: 15px;
    margin: 0;
  }
}

@media (min-width: 768px) {
  .content {
    grid-template-columns: 1fr;
    padding-top: 40px;
    gap: 40px;
    padding-left: 24px;
    padding-right: 24px;
    margin: 0 auto;
  }
}

@media (min-width: 1024px) {

  .content {
    grid-template-columns: minmax(400px, 500px) minmax(500px, 640px);
    padding-top: 60px;
    gap: 12px;
    padding-left: 0;
    padding-right: 0;
    margin: 0 auto;
    width: 100%;
    justify-content: center;
  }
}

@media (min-width: 1200px) {
  .content {
    grid-template-columns: 500px 640px;
    margin: 0 auto;
    width: 100%;
    justify-content: center;
  }
}

.gallery {
  position: sticky;
  top: 12px;
  align-self: start;
  width: 100%;
  height: auto;
}

/* 移动端图片画廊优化 */
@media (max-width: 768px) {
  .gallery {
    position: static;
    order: 1;
    margin-bottom: 0px;
  }
}

@media (max-width: 1023px) {
  .gallery {
    position: static;
    order: 1;
  }
}

.gallery .hero {
  max-width: 100%;
  border-radius: 8px;
  cursor: zoom-in;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .gallery .hero {
    /* margin: 0; */
  }
}


.gallery .hero-blank {
  width: 100%;
  height: auto;
  background: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 18px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-image {
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
}

.hero-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}

.thumbs-wrapper {
  position: relative;
  margin-top: 14px;
}

.thumbs {
  display: grid;
  text-align: center;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-left: auto;
  margin-right: auto;
  scroll-behavior: smooth;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.thumbs::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

@media (min-width: 480px) {
  .thumbs {
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
  }
}

@media (min-width: 768px) {
  .thumbs {
    margin-left: 0;
    margin-right: 0;
  }
}

@media (min-width: 1024px) {
  .thumbs {
    margin-left: 0;
    margin-right: 0;
  }
}

/* 当超过4张时，改为横向滚动布局 */
.thumbs.scrollable {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 6px;
}

.thumbs.scrollable .thumb {
  flex-shrink: 0;
  width: calc((100% - 18px) / 4); /* 4个缩略图，减去3个gap */
  min-width: 100px; /* 最小宽度保证 */
}

.thumb-scroll-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  z-index: 10;
  color: #0e1b4d;
}

.thumb-scroll-btn:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: translateY(-50%) scale(1.1);
}

.thumb-scroll-btn:active {
  transform: translateY(-50%) scale(0.95);
}

.thumb-scroll-left {
  left: -18px;
}

.thumb-scroll-right {
  right: -18px;
}

.thumb {
  aspect-ratio: 1/1;
  background: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
}

.thumb.active {
  border: 1px solid #111;
}

.thumb-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #999;
}

.summary {
  position: relative;
  overflow: visible;
  padding-right: 0;
  order: 1;
  z-index: 1;
}

/* 移动端商品信息优化 */
@media (max-width: 768px) {
  .summary {
    position: static;
    max-height: none;
    overflow: visible;
    order: 2;
    padding: 20px 15px;
    /* background: #fff; */
    border-top: 1px solid #eee;
    /* border-radius: 12px 12px 0 0;
    box-shadow: 0 -2px 10px rgba(0,0,0,0.1); */
    z-index: 10;
  }
}

@media (max-width: 1023px) {
  .summary {
    position: static;
    max-height: none;
    overflow: visible;
    padding-right: 0;
    order: 1;
    text-align: center;
    background-color: transparent;
  }
  
  /* 小屏幕标题居中 */
  .title {
    text-align: center;
    color: #0e1b4d;
  }
  .tt-rating-text, .price-row{
    margin: auto;
    justify-content: center;
  }
  /* 小屏幕价格区域居中 */
  .price-block {
    text-align: center;
  }
  
  .price-row {
    justify-content: center;
  }
  
  /* 小屏幕评分居中 */
  .tt-rating-text {
    justify-content: center;
  }
  
  /* 小屏幕规格选择左对齐 */
  .option {
    text-align: left;
  }
  
  .option-iteme {
    margin: 0.5rem;
    display: inline-flex;
  }
  
  /* 小屏幕数量选择器居中 */
  .quantity {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  /* 小屏幕按钮居中 */
  .actions {
    align-items: center;
  }
}


.title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  line-height: 1.3;
  color: #0e1b4d;
}

/* 移动端标题优化 */
@media (max-width: 768px) {
  .title {
    font-size: 16px;
    line-height: 1.4;
    margin-bottom: 15px;
    text-align: center;
  }
}

@media (min-width: 768px) {
  .title {
    font-size: 24px;
  }
}

@media (min-width: 1024px) {
  .title {
    font-size: 24px;
  }
}

.price-block {
  margin-top: 12px;
}

/* 移动端价格区域优化 */
@media (max-width: 768px) {
  .price-block {
    margin: 15px 0;
    text-align: center;
  }
}

@media (min-width: 768px) {
  .price-block {
    margin-top: 8px;
  }
}

@media (min-width: 1024px) {
  .price-block {
    margin-top: 4px;
  }
}

.price-row {
  display: flex;
  align-items: baseline;
  /* justify-content: space-between; */
  padding: 6px 0;
  font-weight: 400;
  color: #0e1b4d
}

.price-values {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.price-values.has-member-price {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.price-line {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.price-line.non-member {
  color: rgba(14, 27, 77, 0.6);
  font-weight: 500;
}

.price-line.member {
  color: #0e1b4d;
  font-weight: 700;
}

.member-label {
  font-size: 12px;
}

.vip-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.price-row .label {
  color: #0e1b4d;
}

.tt-rating-text {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}


.tt-rating-text .demonstration {
  /* font-size: 14px; */
  color: #0e1b4d;
}

@media (min-width: 768px) {
  .tt-rating-text {
    gap: 4px;
    flex-wrap: nowrap;
  }

  .tt-rating-text .demonstration {
    font-size: 13px;
  }
}



.compare {
  color: #999;
  text-decoration: line-through;
}

.badge {
  border: 1px solid #eee;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  color: #999;
}

.badge.sale {
  color: #c45500;
  border-color: #f0d7c2;
}

.badges {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.unit {
  color: #666;
  font-size: 12px;
  margin-top: 4px;
}

.option {
  margin: 16px 0;
}

@media (min-width: 768px) {
  .option {
    margin: 12px 0;
  }
}

@media (min-width: 1024px) {
  .option {
    margin: 8px 0;
  }
}

.option-iteme {
  display: inline-block;
  margin: .7rem .5rem .2rem 0;
  padding: 12px 20px;
  font-size: 14px;
  letter-spacing: .1rem;
  line-height: 1;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  border-radius: 40px;
  color: #0e1b4d;
  border: 1px solid #0e1b4d;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (min-width: 768px) {
  .option-iteme {
    padding: 8px 26px;
    min-height: auto;
    display: inline-block;
  }
}


.option-item.xz {
  background-color: #0e1b4d;
}

.label {
  font-size: 14px;
  font-weight: 500;
  color: #0e1b4d;
}

.quantity {
  margin: 20px 0;
}

@media (min-width: 768px) {
  .quantity {
    margin: 16px 0;
  }
}

@media (min-width: 1024px) {
  .quantity {
    margin: 12px 0;
  }
}

.quantity-number {
  width: 100px;
  height: 40px;
  border-radius: 26px
}

.quantity-number span {
  background: transparent;
}

.quantity .in-cart {
  margin-top: 4px;
  color: #666;
  font-size: 13px;
}

.actions {
  gap: 12px;
  margin: 20px 0 24px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  justify-content: space-around;
}

/* 移动端操作按钮优化 */
@media (max-width: 768px) {
  .actions {
    flex-direction: column;
    gap: 12px;
    margin: 20px 0;
    justify-content: center;
  }
}

@media (min-width: 480px) {
  .actions {
    gap: 12px;
    margin: 16px 0 20px;
  }
  .option-iteme {
    padding: 8px 26px;
    min-height: auto;
    display: inline-block;
  }
}

@media (min-width: 768px) {
  .actions {
    gap: 8px;
    margin: 12px 0 16px;
  }
}

@media (min-width: 1024px) {
  .actions {
    gap: 8px;
    margin: 30px 0 10px;
  }
}

.actions button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 16px 24px;
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  font-weight: 400;
  height: 48px;
  border-radius: 40px;
  text-decoration: none;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  appearance: none;
  min-width: 120px;
}

/* 移动端按钮优化 */
@media (max-width: 768px) {
  .actions button {
    width: 100%;
    height: 48px;
    font-size: 16px;
    min-width: auto;
    margin-bottom: 8px;
  }
}

@media (min-width: 480px) {
  .actions button {
    /* padding: 16px 32px; */
    height: 44px;
    font-size: 15px;
    width: 50%;
  }
}

@media (min-width: 768px) {
  .actions button {
    /* padding: 16px 42px; */
    /* min-width: 400px; */
    width: 80%;
    font-size: 14px;
  }
}

.actions button:active {
  transform: scale(0.98);
}

.add-to-cart {
  background: transparent;
  border: 1px solid #0e1b4d;
  color: #0e1b4d;
  transition: all 0.3s ease;
}

.add-to-cart:hover:not(:disabled) {
  box-shadow: 0 0 0 2px #0e1b4d;
}

.buy-now {
  background: #4770db;
  border: 1px solid #4770db;
  margin-left: 0px;
  transition: all 0.3s ease;
}

.buy-now:hover:not(:disabled) {
  box-shadow: 0 0 0 2px #4770db;
}

:deep(.el-button.add-to-cart.is-disabled) {
  background: #bbb !important;
  border-color: #bbb !important;
  color: #fff !important;
  opacity: 1;
}

/* 售罄按钮样式 - 只降低透明度，保持原色 */
.sold-out-button.is-disabled {
  cursor: not-allowed;
  opacity: 0.5 !important;
}

:deep(.el-button.buy-now.sold-out-button.is-disabled) {
  cursor: not-allowed;
  opacity: 0.5 !important;
}

:deep(.el-button.add-to-cart.sold-out-button.is-disabled) {
  cursor: not-allowed;
  opacity: 0.5 !important;
}


.product-description {
  margin: 20px 0;
  font-size: 14px;
  line-height: 1.6;
}

/* 移动端商品描述优化 */
@media (max-width: 768px) {
  .product-description {
    margin: 20px 0;
    font-size: 14px;
    line-height: 1.6;
    padding: 0 10px;
  }
}

/* 小屏幕手机优化 - 使用JavaScript检测，避免样式冲突 */
.pdp.small-screen .content {
  padding: 10px 8px;
  gap: 10px;
}

.pdp.small-screen .summary {
  padding: 15px 10px;
}

.pdp.small-screen .title {
  font-size: 18px;
  text-align: center;
}

.pdp.small-screen .option {
  text-align: left;
}

.pdp.small-screen .label {
  display: block;
  width: 100%;
  text-align: left;
}

.pdp.small-screen .option-iteme {
  margin-left: 0;
}

.pdp.small-screen .actions button {
  height: 44px;
  font-size: 15px;
}

.pdp.small-screen .product-description {
  font-size: 13px;
  padding: 0 5px;
}

@media (min-width: 768px) {
  .product-description {
    margin: 25px 0;
    font-size: 15px;
  }
}

@media (min-width: 1024px) {
  .product-description {
    margin: 25px 0;
    font-size: 16px;
  }
}

.product-description :deep(img) {
  width: 100%;
  height: auto;
}



/* tighten divider spacing within summary */
.summary :deep(.el-divider) {
  margin: 16px 0;
}

@media (min-width: 768px) {
  .summary :deep(.el-divider) {
    margin: 14px 0;
  }
}

@media (min-width: 1024px) {
  .summary :deep(.el-divider) {
    margin: 12px 0;
  }
}

.active {
  /* background-color: #0e1b4d; */
  color: #fff;
}

.option-iteme.active {
  background-color: #0e1b4d; 
  color: #fff;
}

/* 售罄规格样式 */
.option-iteme.sold-out {
  color: #bbb;
  border-color: #ddd;
  text-decoration: line-through;
  cursor: not-allowed;
  opacity: 0.6;
}

.option-iteme.sold-out:hover {
  border-color: #ddd;
  box-shadow: none;
}

/* 手机屏幕额外居中样式 */
@media (max-width: 480px) {
  .summary {
    padding: 0 16px;
  }
  
  .title {
     
 
    font-size: 24px !important;
  }
  .tt-rating-text, .price-row{
    margin: auto;
    justify-content: center;
  }
  
  .option-iteme {
    margin: 0.3rem;
    padding: 10px 26px;
    font-size: 13px;
    min-height: auto;
    display: inline-block;
  }
  
   .actions button {
    width: 80%;
  } 
}

@media screen and (min-width: 1024px) {
  .summary {
    margin-left: 50px;
    padding-left: 10px;
  }
}

/* 图片预览弹出框层级设置 */
:deep(.el-image-viewer__wrapper) {
  z-index: 9999 !important;
}

:deep(.el-image-viewer__mask) {
  z-index: 9998 !important;
}

/* 放大按钮样式 */
.zoom-button {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: 0;
  transform: scale(0.8);
  z-index: 10;
  pointer-events: none;
}

/* 移动端常显示 */
.zoom-button.always-visible {
  opacity: 1;
  transform: scale(1);
}

/* PC端移入显示 */
.zoom-button.hover-visible {
  opacity: 1;
  transform: scale(1);
}

.zoom-button:hover {
  background: rgba(255, 255, 255, 1);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.zoom-button svg {
  transition: transform 0.2s ease;
}

.zoom-button:hover svg {
  transform: scale(1.1);
}

/* 移动端优化 */
@media (max-width: 768px) {
  .zoom-button {
    width: 36px;
    height: 36px;
    top: 10px;
    right: 10px;
  }
  
  .zoom-button svg {
    width: 20px;
    height: 20px;
  }
}

/* 移动端页码指示器样式 */
.mobile-pagination {
  margin-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 16px;
 
}

.nav-arrow {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  color: #666;
}

.nav-arrow:hover:not(:disabled) {
  background: rgba(14, 27, 77, 0.1);
  color: #0e1b4d;
  transform: scale(1.1);
}

.nav-arrow:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.nav-arrow:active:not(:disabled) {
  transform: scale(0.95);
}

.page-indicator {
  font-size: 12px;
  font-weight: 500;
  color: #505a7c;
  min-width: 40px;
  text-align: center;
  user-select: none;
}

/* 隐藏PC端的页码指示器 */
@media (min-width: 769px) {
  .mobile-pagination {
    display: none;
  }
}

/* 弹窗模式下的内容高度 */
.content.modal-content {
  height: 70vh;
  max-height: none;
  overflow: scroll;
}
</style>
